import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Code, Copy, Check } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface PiiEmbedButtonProps {
  chatbotId: string;
}

export function PiiEmbedButton({ chatbotId }: PiiEmbedButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      toast({
        title: "Copied to clipboard",
        description: "Code example has been copied to your clipboard.",
      });
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please copy the code manually.",
        variant: "destructive",
      });
    }
  };

  // Generate example code for different use cases
  const generateFormExample = () => {
    return `<!-- HTML Form Example -->
<form id="pii-form">
  <input type="text" id="name" placeholder="Name" required>
  <input type="email" id="email" placeholder="Email" required>
  <input type="tel" id="phone" placeholder="Phone">
  <button type="submit">Start Chat</button>
</form>

<script>
document.getElementById('pii-form').addEventListener('submit', async function(e) {
  e.preventDefault();

  const formData = {
    name: document.getElementById('name').value,
    email: document.getElementById('email').value,
    phone: document.getElementById('phone').value
  };

  try {
    const response = await fetch('https://app.offerchat.ai/functions/v1/pii-manager', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: formData, expiresInDays: 30 })
    });

    const result = await response.json();
    window.location.href = \`https://app.offerchat.ai/chat/${chatbotId}?pii=\${result.token}\`;
  } catch (error) {
    alert('Error: ' + error.message);
  }
});
</script>`;
  };

  const generateApiExample = () => {
    return `// JavaScript API Example
async function createPiiToken(piiData) {
  const response = await fetch('https://app.offerchat.ai/functions/v1/pii-manager', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: piiData, expiresInDays: 30 })
  });

  if (!response.ok) throw new Error('Failed to create PII token');
  return await response.json();
}

// Usage
const userData = {
  name: 'John Doe',
  email: 'john@example.com',
  company: 'Acme Inc.'
};

createPiiToken(userData)
  .then(result => {
    window.location.href = \`https://app.offerchat.ai/chat/${chatbotId}?pii=\${result.token}\`;
  })
  .catch(error => alert('Error: ' + error.message));`;
  };

  const generateEmbedExample = () => {
    return `<!-- Dynamic Token Creation -->
<script>
async function loadWithPII() {
  const response = await fetch('https://app.offerchat.ai/functions/v1/pii-manager', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      data: { name: 'John', email: 'john@example.com' },
      expiresInDays: 30
    })
  });

  const { token } = await response.json();

  const script = document.createElement('script');
  script.src = 'https://app.offerchat.ai/embed.js';
  script.setAttribute('data-chatbot-id', '${chatbotId}');
  script.setAttribute('data-pii-token', token);
  document.body.appendChild(script);
}
loadWithPII();
</script>`;
  };

  const generateVariableExample = () => {
    return `// Using PII Variables in Questions

// Basic usage with default values:
"Welcome {{name:there}}! How can I help you today?"
"Is {{email}} still your preferred contact?"

// Multiple variables:
"Hi {{name:visitor}} from {{company:your company}}!"

// Example PII data structure:
{
  "name": "John Doe",
  "email": "john@example.com",
  "company": "Acme Inc.",
  "plan": "Premium"
}

// Best practices:
// • Always provide default values: {{variable:default}}
// • Variables are case-sensitive
// • Use only alphanumeric and underscore characters`;
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2"
      >
        <Code size={16} />
        <span>PII Embed</span>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>PII Embed Examples</DialogTitle>
            <DialogDescription className="text-sm">
              Pass user data to your chatbot using PII tokens, auto-extract from URL parameters, or use variables.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            <Tabs defaultValue="embed" className="h-full flex flex-col">
              <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
                <TabsTrigger value="embed">Embed</TabsTrigger>
                <TabsTrigger value="form">Form</TabsTrigger>
                <TabsTrigger value="api">API</TabsTrigger>
                <TabsTrigger value="variables">Variables</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto mt-3">
                <TabsContent value="embed" className="mt-0 h-full">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">Embed Script with PII</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(generateEmbedExample(), 'embed')}
                        className="h-8 px-2"
                      >
                        {copiedStates.embed ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Embed the chat widget with PII tokens or auto-extract user data from URL parameters.
                    </p>
                    <div className="bg-gray-900 rounded-md overflow-hidden">
                      <pre className="text-gray-100 p-3 text-xs overflow-auto max-h-[400px] whitespace-pre-wrap">
                        {generateEmbedExample()}
                      </pre>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="form" className="mt-0 h-full">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">HTML Form Example</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(generateFormExample(), 'form')}
                        className="h-8 px-2"
                      >
                        {copiedStates.form ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Create a form that collects user data and redirects to your chatbot.
                    </p>
                    <div className="bg-gray-900 rounded-md overflow-hidden">
                      <pre className="text-gray-100 p-3 text-xs overflow-auto max-h-[400px] whitespace-pre-wrap">
                        {generateFormExample()}
                      </pre>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="api" className="mt-0 h-full">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">JavaScript API</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(generateApiExample(), 'api')}
                        className="h-8 px-2"
                      >
                        {copiedStates.api ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Create PII tokens programmatically using JavaScript.
                    </p>
                    <div className="bg-gray-900 rounded-md overflow-hidden">
                      <pre className="text-gray-100 p-3 text-xs overflow-auto max-h-[400px] whitespace-pre-wrap">
                        {generateApiExample()}
                      </pre>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="variables" className="mt-0 h-full">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">Variable Usage</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(generateVariableExample(), 'variables')}
                        className="h-8 px-2"
                      >
                        {copiedStates.variables ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Use PII variables in your chatbot questions.
                    </p>
                    <div className="bg-gray-900 rounded-md overflow-hidden">
                      <pre className="text-gray-100 p-3 text-xs overflow-auto max-h-[400px] whitespace-pre-wrap">
                        {generateVariableExample()}
                      </pre>
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PiiEmbedButton;
